/**
 * Чек-лист начала работы — макет 25:2: «Начало работы · 2 из 4» с изумрудным
 * прогрессом.
 *
 * Три шага из четырёх видны по данным: проект есть в списке, задача — в
 * выдаче списка задач, второй участник — в составе проекта. Четвёртый,
 * «настройте колонки», данными не проверяется: колонки у проекта есть всегда
 * (их ставит пресет при создании), а признака «их трогали руками» в модели
 * нет. Его отмечает сам экран настроек — то есть факт действия, а не
 * состояние сервера (docs/design-gaps.md).
 */
import { projectApi } from '@/api'
import { listIssues } from '@/features/issues/api'
import { emptyFilters } from '@/features/issues/filters'

const COLUMNS_KEY = 'tracker-onboarding-columns'
const DONE_KEY = 'tracker-onboarding-done'

export type StepKey = 'project' | 'issue' | 'columns' | 'team'

export interface Step {
  key: StepKey
  done: boolean
}

export interface Progress {
  hasProject: boolean
  hasIssue: boolean
  hasTeam: boolean
  touchedColumns: boolean
}

export const markColumnsTouched = (): void => {
  if (typeof window !== 'undefined') window.localStorage.setItem(COLUMNS_KEY, '1')
}

const columnsTouched = (): boolean =>
  typeof window !== 'undefined' && window.localStorage.getItem(COLUMNS_KEY) === '1'

/** Пройденный чек-лист больше не показываем: он подсказка, а не отчёт. */
export const isDismissed = (): boolean =>
  typeof window !== 'undefined' && window.localStorage.getItem(DONE_KEY) === '1'

export const dismiss = (): void => {
  if (typeof window !== 'undefined') window.localStorage.setItem(DONE_KEY, '1')
}

export const buildSteps = (progress: Progress): Step[] => [
  { key: 'project', done: progress.hasProject },
  { key: 'issue', done: progress.hasIssue },
  { key: 'columns', done: progress.touchedColumns },
  { key: 'team', done: progress.hasTeam },
]

export const doneCount = (steps: Step[]): number => steps.filter((step) => step.done).length

/**
 * Состояние по первому проекту: у новичка он один, а спрашивать по каждому
 * значит два запроса на проект ради подсказки, которая исчезнет через день.
 */
export const loadProgress = async (projectId: string): Promise<Progress> => {
  const base: Progress = {
    hasProject: Boolean(projectId),
    hasIssue: false,
    hasTeam: false,
    touchedColumns: columnsTouched(),
  }

  if (!projectId) return base

  // Одна задача на странице: нужен только счётчик, а не список.
  const [issues, members] = await Promise.all([
    listIssues(projectId, emptyFilters(), { field: 'key', desc: false }, 1, 1),
    projectApi.projectServiceListProjectMembers(projectId),
  ])

  return {
    ...base,
    hasIssue: issues.total > 0,
    hasTeam: (members.data.members ?? []).length > 1,
  }
}
